import { Activity, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import { logoUrl, useBrandingStatus } from '../../features/branding/useBranding';
import { ErrorBoundary } from '../../components/ui/ErrorBoundary';
import { ThemeToggle } from '../../features/theme/ThemeToggle';
import { Sidebar } from './Sidebar';

// Человекочитаемые названия сегментов маршрута для «хлебных крошек». Сегменты,
// которых тут нет (UUID/числовые id), выкидываются из подписи — вместо длинного
// технического пути пользователь видит понятную цепочку.
const SEGMENT_LABELS: Record<string, string> = {
  overview: 'Обзор',
  sales: 'Продажи',
  dealers: 'Дилеры',
  products: 'Модели',
  logistics: 'Логистика',
  positions: 'Карточка поставки',
  inventory: 'Остатки',
  quotas: 'Квоты',
  analytics: 'Аналитика',
  'ai-analytics': 'AI-аналитика',
  marketing: 'Маркетинг',
  handling: 'Детальная аналитика',
  leads: 'Лиды',
  settings: 'Настройки',
  integrations: 'Интеграции',
  shipments: 'Отгрузки',
  'stock-optimization': 'Оптимизация склада',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Превращает URL-путь в понятную цепочку: `/logistics/positions/<uuid>` →
 * «Логистика › Карточка поставки». Технические id (UUID/числа) отбрасываются.
 */
export function humanizePath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  const labels: string[] = [];
  for (const seg of parts) {
    if (UUID_RE.test(seg) || /^\d+$/.test(seg)) continue;
    labels.push(SEGMENT_LABELS[seg] ?? seg);
  }
  return labels.join(' › ') || 'Обзор';
}

// Страница может задать свою подпись крошек (напр. с номером контракта) через
// useBreadcrumb — она перекрывает автоматическую для своего пути.
const BreadcrumbContext = createContext<{
  crumb: { path: string; label: string } | null;
  set: (path: string, label: string | null) => void;
}>({ crumb: null, set: () => {} });

/** Задаёт подпись «хлебных крошек» для текущей страницы (сбрасывается при уходе). */
export function useBreadcrumb(label: string | null): void {
  const { set } = useContext(BreadcrumbContext);
  const { pathname } = useLocation();
  useEffect(() => {
    set(pathname, label);
    return () => set(pathname, null);
  }, [pathname, label, set]);
}

/**
 * Глобальная навигация «Назад/Вперёд» по истории браузера — доступна на всех
 * страницах. Возврат ведёт на ТУ ЖЕ страницу с её состоянием (URL из истории),
 * а не на дефолт. «Назад» отключается на самой первой записи истории.
 */
function HistoryNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { crumb } = useContext(BreadcrumbContext);
  // React Router кладёт индекс записи в history.state.idx.
  const idx =
    (typeof window !== 'undefined' && (window.history.state as { idx?: number } | null)?.idx) || 0;
  const canBack = idx > 0 || (typeof window !== 'undefined' && window.history.length > 1);
  // Подпись пути: переопределение страницы (если задано для этого URL) или
  // автоматическая человекочитаемая цепочка без технических id.
  const label =
    crumb && crumb.path === location.pathname ? crumb.label : humanizePath(location.pathname);

  return (
    <div className="flex items-center gap-1 border-b border-surface-border bg-surface/60 px-2 py-1.5 backdrop-blur">
      <button
        type="button"
        onClick={() => navigate(-1)}
        disabled={!canBack}
        className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-slate-200 transition hover:bg-white/5 hover:text-slate-100 disabled:opacity-50 disabled:hover:bg-transparent disabled:text-slate-400"
        aria-label="Назад"
      >
        <ChevronLeft size={16} /> Назад
      </button>
      <button
        type="button"
        onClick={() => navigate(1)}
        className="inline-flex items-center rounded-lg p-1.5 text-slate-300 transition hover:bg-white/5 hover:text-slate-100"
        aria-label="Вперёд"
      >
        <ChevronRight size={16} />
      </button>
      <span className="ml-1 truncate text-[11px] font-medium text-slate-400">{label}</span>
    </div>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Свёрнутое меню (только иконки) — состояние помним между сессиями.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === '1',
  );
  const toggleCollapsed = () =>
    setCollapsed((v) => {
      localStorage.setItem('sidebarCollapsed', v ? '0' : '1');
      return !v;
    });
  const { data: branding } = useBrandingStatus();
  const customLogo = logoUrl(branding);
  const [crumb, setCrumb] = useState<{ path: string; label: string } | null>(null);
  const setBreadcrumb = useCallback((path: string, label: string | null) => {
    setCrumb((prev) => {
      if (label == null) return prev && prev.path === path ? null : prev;
      return { path, label };
    });
  }, []);

  if (!user) return null;

  return (
    <BreadcrumbContext.Provider value={{ crumb, set: setBreadcrumb }}>
      <div className="flex h-screen overflow-hidden">
        <div className="hidden md:block">
          <Sidebar
            user={user}
            onLogout={logout}
            collapsed={collapsed}
            onToggleCollapse={toggleCollapsed}
          />
        </div>

        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <button
              type="button"
              aria-label="Закрыть меню"
              className="absolute inset-0 bg-black/60"
              onClick={() => setMobileOpen(false)}
            />
            <div className="relative z-10 h-full">
              <Sidebar user={user} onLogout={logout} onNavigate={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-center justify-between border-b border-surface-border px-4 py-3 md:hidden">
            <div className="flex items-center gap-2">
              {customLogo ? (
                <img src={customLogo} alt="Логотип" className="h-7 w-7 rounded-lg object-contain" />
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-accent to-brand-violet text-white">
                  <Activity size={15} />
                </div>
              )}
              <span className="text-sm font-semibold text-slate-100">VOGE DMS</span>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle compact />
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="rounded-md p-2 text-slate-300 hover:bg-white/5"
                aria-label="Открыть меню"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </header>

          <HistoryNav />

          <main className="flex-1 overflow-y-auto p-4 md:p-8">
            <ErrorBoundary
              key={location.pathname}
              fallbackTitle="Не удалось отобразить раздел"
              fallbackMessage="Произошла непредвиденная ошибка при загрузке страницы. Попробуйте обновить."
            >
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </BreadcrumbContext.Provider>
  );
}
